import React, { useState, useEffect } from 'react';
import '../styles/Gallery.css';

interface SuperheroImage {
  key: string;
  size: number;
  uploaded: string;
  url: string;
}

interface GalleryProps {
  onSelectImage?: (image: SuperheroImage) => void;
}

const Gallery: React.FC<GalleryProps> = ({ onSelectImage }) => {
  const [images, setImages] = useState<SuperheroImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchImages = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Fetching superhero images from API...');
        const response = await fetch('/api/superhero-images/list');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch images: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Received image data:', data);
        setImages(data.images || []);
      } catch (err) {
        console.error('Error fetching superhero images:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchImages();
  }, []);

  const handleImageClick = (image: SuperheroImage) => {
    if (onSelectImage) {
      onSelectImage(image);
    }
  };

  if (isLoading) {
    return (
      <div className="gallery-loading">
        <div className="loading-spinner"></div>
        <p>Loading superhero gallery...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gallery-error">
        <span className="error-icon">⚠️</span>
        <p>Error loading gallery: {error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="gallery-empty">
        <p>No superhero images found. Create one first!</p>
      </div>
    );
  }

  return (
    <div className="gallery-container">
      <h1 className="gallery-title">Superhero Gallery</h1>
      <p className="gallery-subtitle">Browse all superhero transformations</p>
      
      <div className="gallery-grid">
        {images.map((image) => (
          <div key={image.key} className="gallery-item" onClick={() => handleImageClick(image)}>
            <div className="gallery-image-container">
              <img 
                src={image.url} 
                alt={`Superhero ${image.key}`} 
                className="gallery-image" 
                loading="lazy"
              />
            </div>
            <div className="gallery-image-info">
              <span className="image-date">{new Date(image.uploaded).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Gallery;